import AddDocumentListItem from '@Application/Beneficiaries/Documents/Commands/AddDocumentListItem';
import MoveDocumentListItem from '@Application/Beneficiaries/Documents/Commands/MoveDocumentListItem';
import RemoveDocumentListItem from '@Application/Beneficiaries/Documents/Commands/RemoveDocumentListItem';
import addDocumentListItem from '@Application/Beneficiaries/Documents/addDocumentListItem';
import moveDocumentListItem from '@Application/Beneficiaries/Documents/moveDocumentListItem';
import removeDocumentListItem from '@Application/Beneficiaries/Documents/removeDocumentListItem';
import type { DocumentEntryView } from '@UI/Store/Documents/Views/buildDocumentEntryView';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import type { ViewerListAction } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/ViewerListAction';
import documentPanelSignal, {
	openDocumentPanel,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import { setPendingDiscard } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import * as React from 'react';
import { useDispatch, useSelector } from '@verclam/synapse';

type RemoveAction = Extract<ViewerListAction, { readonly kind: 'remove' }>;

/** A list gesture waiting for the user's confirmation. */
export type PendingListConfirmation =
	| {
			readonly type: 'remove';
			readonly action: RemoveAction;
			readonly itemName: string;
			readonly isDocumentVerified: boolean;
	  }
	| { readonly type: 'verified'; readonly action: ViewerListAction };

export type ListActions = {
	/** The one entry point of every list gesture, from the panels and from the viewer's bars. */
	readonly requestListAction: (action: ViewerListAction) => void;
	/** `true` while a gesture is in flight: a `POST …/items` is not idempotent. */
	readonly isBusy: boolean;
	readonly confirmation: PendingListConfirmation | null;
	readonly acceptConfirmation: () => void;
	readonly dismissConfirmation: () => void;
};

/** Where the element at `index` sits once `from` has been swapped with `to` (adjacent slots). */
const resolveIndexAfterMove = (index: number, from: number, to: number): number => {
	if (index === from) {
		return to;
	}

	return index === to ? from : index;
};

/** The first field of an element in document order, groups included; `null` when it holds none. */
const findFirstFieldKey = (entries: readonly DocumentEntryView[]): string | null => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			return entry.key;
		}

		if (entry.type === 'group') {
			const nested = findFirstFieldKey(entry.entries);

			if (nested !== null) {
				return nested;
			}
		}
	}

	return null;
};

/**
 * Runs the document's list gestures (add, remove, move) and registers itself as the viewer's list
 * handler. A modified draft queues a `list-action` discard request first; a removal and a gesture on a
 * verified document ask for confirmation; while a call is in flight every further gesture is ignored.
 * Afterwards the open panel follows its element, turns `gone` when it was removed, or opens on the first
 * field of the new element (its own panel when it has none).
 */
const useListActions = (): ListActions => {
	const dispatch = useDispatch();
	const document = useSelector((state) => state.documentsStore.document);
	const { facilityId, subject, documentId } = useDocumentRoute();
	const canEdit = useCanEditDocument();
	const { listActionHandlerRef, revealItem } = useDocumentViewer();

	const [isBusy, setIsBusy] = React.useState(false);
	const [confirmation, setConfirmation] = React.useState<PendingListConfirmation | null>(null);
	const isBusyRef = React.useRef(false);
	const pendingRevealRef = React.useRef<{
		readonly listKey: string;
		readonly index: number;
		/** Set by an addition: the list length once the new element is in the store. */
		readonly expectedCount: number | null;
	} | null>(null);

	// The viewer applies the new document before this effect runs, so the element can be located then. The
	// panel of an added element opens here rather than in the call's `then`: this effect always reads the
	// document that holds the element, whereas the closure of `then` may still hold the previous one.
	React.useEffect(() => {
		const pending = pendingRevealRef.current;

		if (document === null || pending === null) {
			return;
		}

		if (pending.expectedCount !== null) {
			const list = document.findListByKey(pending.listKey);

			if (list === null || list.items.length !== pending.expectedCount) {
				return;
			}

			const firstFieldKey = findFirstFieldKey(list.items[pending.index]?.entries ?? []);

			openDocumentPanel(
				firstFieldKey === null
					? { type: 'list-item', listKey: pending.listKey, index: pending.index }
					: { type: 'field', fieldKey: firstFieldKey, occurrenceKey: null },
			);
		}

		pendingRevealRef.current = null;
		revealItem(pending.listKey, pending.index);
	}, [document, revealItem]);

	const execute = React.useCallback(
		(action: ViewerListAction) => {
			isBusyRef.current = true;
			setIsBusy(true);

			const finish = () => {
				isBusyRef.current = false;
				setIsBusy(false);
			};
			const open = documentPanelSignal.peek();
			const openItem = open?.type === 'list-item' && open.listKey === action.listKey ? open : null;

			if (action.kind === 'add') {
				pendingRevealRef.current = {
					listKey: action.listKey,
					index: action.position,
					expectedCount: (document?.findListByKey(action.listKey)?.items.length ?? 0) + 1,
				};
				dispatch(
					addDocumentListItem(
						new AddDocumentListItem(
							facilityId,
							subject,
							documentId,
							action.listKey,
							action.position,
						),
					),
				)
					.catch(() => {
						pendingRevealRef.current = null;
					})
					.finally(finish);
				return;
			}

			if (action.kind === 'remove') {
				dispatch(
					removeDocumentListItem(
						new RemoveDocumentListItem(
							facilityId,
							subject,
							documentId,
							action.listKey,
							action.index,
						),
					),
				)
					.then(() => {
						if (openItem !== null) {
							openDocumentPanel({ type: 'gone', reason: 'item' });
						}
					})
					.catch(ignoreHandledError)
					.finally(finish);
				return;
			}

			pendingRevealRef.current = { listKey: action.listKey, index: action.to, expectedCount: null };
			dispatch(
				moveDocumentListItem(
					new MoveDocumentListItem(
						facilityId,
						subject,
						documentId,
						action.listKey,
						action.from,
						action.to,
					),
				),
			)
				.then(() => {
					if (openItem !== null) {
						openDocumentPanel({
							type: 'list-item',
							listKey: action.listKey,
							index: resolveIndexAfterMove(openItem.index, action.from, action.to),
						});
					}
				})
				.catch(() => {
					pendingRevealRef.current = null;
				})
				.finally(finish);
		},
		[dispatch, document, facilityId, subject, documentId],
	);

	const requestListAction = React.useCallback(
		(action: ViewerListAction) => {
			if (!canEdit || document === null || isBusyRef.current) {
				return;
			}

			if (editingFieldSignal.peek()?.isDirty) {
				setPendingDiscard({ type: 'list-action', action });
				return;
			}

			const isDocumentVerified = document.status.value === 'verified';

			if (action.kind === 'remove') {
				const list = document.findListByKey(action.listKey);

				if (list !== null) {
					setConfirmation({
						type: 'remove',
						action,
						itemName: `${list.title} ${action.index + 1}`,
						isDocumentVerified,
					});
				}

				return;
			}

			if (isDocumentVerified) {
				setConfirmation({ type: 'verified', action });
				return;
			}

			execute(action);
		},
		[canEdit, document, execute],
	);

	React.useEffect(() => {
		listActionHandlerRef.current = requestListAction;

		return () => {
			listActionHandlerRef.current = null;
		};
	}, [listActionHandlerRef, requestListAction]);

	const acceptConfirmation = React.useCallback(() => {
		if (confirmation === null) {
			return;
		}

		setConfirmation(null);
		execute(confirmation.action);
	}, [confirmation, execute]);

	const dismissConfirmation = React.useCallback(() => {
		setConfirmation(null);
	}, []);

	return { requestListAction, isBusy, confirmation, acceptConfirmation, dismissConfirmation };
};

export default useListActions;
